import React, { useState } from 'react';

// ─── Theme Definitions ──────────────────────────────────────────────────────

interface ThemeConfig {
  id: string;
  name: string;
  tag: string;
  description: string;
  // Page background
  pageBg: string;
  pageText: string;
  // Sidebar / left panel
  sidebarBg: string;
  sidebarText: string;
  sidebarSubtext: string;
  sidebarBorder: string;
  // Right / form panel
  formBg: string;
  formBorder: string;
  formText: string;
  formSubtext: string;
  // Accent (button, active, highlights)
  accent: string;
  accentText: string;
  accentHover: string;
  // Input field
  inputBg: string;
  inputBorder: string;
  inputText: string;
  inputPlaceholder: string;
  // Card
  cardBg: string;
  cardBorder: string;
  cardText: string;
  // Badge pill
  badgeBg: string;
  badgeText: string;
  // Divider
  divider: string;
  // Nav active
  navActiveBg: string;
  navActiveText: string;
  navHoverBg: string;
  navText: string;
}

const THEMES: ThemeConfig[] = [
  {
    id: 'dark-luxury',
    name: 'Dark Luxury',
    tag: 'Current',
    description: 'Sophisticated black with warm rose-gold accents. Premium, authoritative.',
    pageBg: '#141414',
    pageText: '#FAF8F5',
    sidebarBg: '#1E1E1E',
    sidebarText: '#FAF8F5',
    sidebarSubtext: '#9E9E9E',
    sidebarBorder: '#2C2C2C',
    formBg: '#1A1A1A',
    formBorder: '#2C2C2C',
    formText: '#FAF8F5',
    formSubtext: '#9E9E9E',
    accent: '#C4956A',
    accentText: '#141414',
    accentHover: '#D4A57A',
    inputBg: '#2C2C2C',
    inputBorder: '#3C3C3C',
    inputText: '#FAF8F5',
    inputPlaceholder: '#6B6B6B',
    cardBg: '#242424',
    cardBorder: '#333333',
    cardText: '#E0DDD8',
    badgeBg: 'rgba(196,149,106,0.15)',
    badgeText: '#C4956A',
    divider: '#2C2C2C',
    navActiveBg: 'rgba(196,149,106,0.15)',
    navActiveText: '#C4956A',
    navHoverBg: 'rgba(255,255,255,0.05)',
    navText: '#9E9E9E',
  },
  {
    id: 'pearl-white',
    name: 'Pearl White',
    tag: 'Light',
    description: 'Clean ivory with rose-gold accents. Professional, airy, corporate.',
    pageBg: '#F8F6F2',
    pageText: '#1A1A1A',
    sidebarBg: '#FFFFFF',
    sidebarText: '#1A1A1A',
    sidebarSubtext: '#666666',
    sidebarBorder: '#E8E4DE',
    formBg: '#FFFFFF',
    formBorder: '#E8E4DE',
    formText: '#1A1A1A',
    formSubtext: '#777777',
    accent: '#C4956A',
    accentText: '#FFFFFF',
    accentHover: '#B07F55',
    inputBg: '#F8F6F2',
    inputBorder: '#DDD8D0',
    inputText: '#1A1A1A',
    inputPlaceholder: '#AAAAAA',
    cardBg: '#FFFFFF',
    cardBorder: '#EAE6E0',
    cardText: '#444444',
    badgeBg: 'rgba(196,149,106,0.12)',
    badgeText: '#A07040',
    divider: '#EAE6E0',
    navActiveBg: 'rgba(196,149,106,0.1)',
    navActiveText: '#A07040',
    navHoverBg: 'rgba(0,0,0,0.04)',
    navText: '#666666',
  },
  {
    id: 'deep-navy',
    name: 'Deep Navy',
    tag: 'Dark',
    description: 'Rich navy blue with warm gold. Authoritative, trust-inspiring, classic.',
    pageBg: '#0A1628',
    pageText: '#E8E0D0',
    sidebarBg: '#0F1E38',
    sidebarText: '#E8E0D0',
    sidebarSubtext: '#8A9BBB',
    sidebarBorder: '#1A2D4E',
    formBg: '#0D1A30',
    formBorder: '#1A2D4E',
    formText: '#E8E0D0',
    formSubtext: '#8A9BBB',
    accent: '#D4AF37',
    accentText: '#0A1628',
    accentHover: '#E4BF47',
    inputBg: '#162338',
    inputBorder: '#243655',
    inputText: '#E8E0D0',
    inputPlaceholder: '#5A6E8A',
    cardBg: '#132035',
    cardBorder: '#1E3050',
    cardText: '#C8C0B0',
    badgeBg: 'rgba(212,175,55,0.15)',
    badgeText: '#D4AF37',
    divider: '#1A2D4E',
    navActiveBg: 'rgba(212,175,55,0.12)',
    navActiveText: '#D4AF37',
    navHoverBg: 'rgba(255,255,255,0.05)',
    navText: '#8A9BBB',
  },
  {
    id: 'warm-ivory',
    name: 'Warm Ivory',
    tag: 'Light',
    description: 'Parchment-warm tones with bronze & terracotta. Elegant, calm, timeless.',
    pageBg: '#FDF6ED',
    pageText: '#2C1810',
    sidebarBg: '#FFF9F2',
    sidebarText: '#2C1810',
    sidebarSubtext: '#7A5C4A',
    sidebarBorder: '#EDE0D0',
    formBg: '#FFFBF6',
    formBorder: '#EDE0D0',
    formText: '#2C1810',
    formSubtext: '#8A6654',
    accent: '#B5601A',
    accentText: '#FFFFFF',
    accentHover: '#C5701A',
    inputBg: '#FDF6ED',
    inputBorder: '#DCC8B0',
    inputText: '#2C1810',
    inputPlaceholder: '#B09080',
    cardBg: '#FFF9F2',
    cardBorder: '#EDE0D0',
    cardText: '#5A3C28',
    badgeBg: 'rgba(181,96,26,0.1)',
    badgeText: '#8A4010',
    divider: '#EDE0D0',
    navActiveBg: 'rgba(181,96,26,0.1)',
    navActiveText: '#8A4010',
    navHoverBg: 'rgba(44,24,16,0.05)',
    navText: '#7A5C4A',
  },
  {
    id: 'sage-green',
    name: 'Sage Green',
    tag: 'Dark',
    description: 'Deep forest green with warm gold. Fresh, trustworthy, nature-inspired.',
    pageBg: '#0E1F18',
    pageText: '#E4EDE6',
    sidebarBg: '#122218',
    sidebarText: '#E4EDE6',
    sidebarSubtext: '#7AA88A',
    sidebarBorder: '#1E3828',
    formBg: '#102018',
    formBorder: '#1E3828',
    formText: '#E4EDE6',
    formSubtext: '#7AA88A',
    accent: '#C8A84B',
    accentText: '#0E1F18',
    accentHover: '#D8B85B',
    inputBg: '#183020',
    inputBorder: '#264838',
    inputText: '#E4EDE6',
    inputPlaceholder: '#4A7858',
    cardBg: '#152A1C',
    cardBorder: '#1E3828',
    cardText: '#C0D8C0',
    badgeBg: 'rgba(200,168,75,0.15)',
    badgeText: '#C8A84B',
    divider: '#1E3828',
    navActiveBg: 'rgba(200,168,75,0.12)',
    navActiveText: '#C8A84B',
    navHoverBg: 'rgba(255,255,255,0.05)',
    navText: '#7AA88A',
  },
  {
    id: 'blush-rose',
    name: 'Blush Rose',
    tag: 'Light',
    description: 'Soft dusty rose with mauve & gold. Warm, approachable, modern wellness.',
    pageBg: '#FDF5F5',
    pageText: '#2A1A1A',
    sidebarBg: '#FFF8F8',
    sidebarText: '#2A1A1A',
    sidebarSubtext: '#8A6060',
    sidebarBorder: '#F0DCDC',
    formBg: '#FFFAFA',
    formBorder: '#F0DCDC',
    formText: '#2A1A1A',
    formSubtext: '#8A6060',
    accent: '#C4607A',
    accentText: '#FFFFFF',
    accentHover: '#D4708A',
    inputBg: '#FDF5F5',
    inputBorder: '#E8D0D0',
    inputText: '#2A1A1A',
    inputPlaceholder: '#B89090',
    cardBg: '#FFF8F8',
    cardBorder: '#F0DCDC',
    cardText: '#5A3838',
    badgeBg: 'rgba(196,96,122,0.1)',
    badgeText: '#A04060',
    divider: '#F0DCDC',
    navActiveBg: 'rgba(196,96,122,0.1)',
    navActiveText: '#A04060',
    navHoverBg: 'rgba(42,26,26,0.04)',
    navText: '#8A6060',
  },
  {
    id: 'slate-grey',
    name: 'Slate Grey',
    tag: 'Neutral',
    description: 'Cool mid-grey with teal & gold. Modern, tech-forward, clean.',
    pageBg: '#1C2128',
    pageText: '#CDD2DA',
    sidebarBg: '#22272E',
    sidebarText: '#CDD2DA',
    sidebarSubtext: '#768390',
    sidebarBorder: '#2D333B',
    formBg: '#1E2430',
    formBorder: '#2D333B',
    formText: '#CDD2DA',
    formSubtext: '#768390',
    accent: '#39B5A4',
    accentText: '#0D1117',
    accentHover: '#49C5B4',
    inputBg: '#2D333B',
    inputBorder: '#444C56',
    inputText: '#CDD2DA',
    inputPlaceholder: '#545D68',
    cardBg: '#282F38',
    cardBorder: '#363E47',
    cardText: '#ADBAC7',
    badgeBg: 'rgba(57,181,164,0.15)',
    badgeText: '#39B5A4',
    divider: '#2D333B',
    navActiveBg: 'rgba(57,181,164,0.12)',
    navActiveText: '#39B5A4',
    navHoverBg: 'rgba(255,255,255,0.05)',
    navText: '#768390',
  },
];

// ─── Mini UI Mock Components ──────────────────────────────────────────────

const NAV_ITEMS = [
  { label: 'Dashboard', icon: '◈' },
  { label: 'Assessment', icon: '◎' },
  { label: 'Evidence', icon: '▣' },
  { label: 'Reports', icon: '▤' },
  { label: 'Settings', icon: '◉' },
];

function MiniSidebar({ t }: { t: ThemeConfig }) {
  return (
    <div
      style={{
        background: t.sidebarBg,
        borderRight: `1px solid ${t.sidebarBorder}`,
        width: 120,
        padding: '12px 0',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        flexShrink: 0,
      }}
    >
      {/* Logo area */}
      <div
        style={{
          padding: '8px 10px 12px',
          borderBottom: `1px solid ${t.sidebarBorder}`,
          marginBottom: 6,
        }}
      >
        <div style={{ fontSize: 11, fontWeight: 700, color: t.accent, letterSpacing: 2 }}>AVIKA</div>
        <div style={{ fontSize: 8, color: t.sidebarSubtext, marginTop: 1 }}>POSH Platform</div>
      </div>
      {NAV_ITEMS.map((item, i) => (
        <div
          key={item.label}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '5px 10px',
            margin: '0 6px',
            borderRadius: 4,
            background: i === 0 ? t.navActiveBg : 'transparent',
            cursor: 'pointer',
          }}
        >
          <span style={{ fontSize: 9, color: i === 0 ? t.navActiveText : t.navText }}>{item.icon}</span>
          <span
            style={{
              fontSize: 9,
              color: i === 0 ? t.navActiveText : t.navText,
              fontWeight: i === 0 ? 600 : 400,
            }}
          >
            {item.label}
          </span>
        </div>
      ))}
    </div>
  );
}

function MiniDashboard({ t }: { t: ThemeConfig }) {
  return (
    <div style={{ flex: 1, background: t.pageBg, padding: 10, overflow: 'hidden' }}>
      {/* Header bar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 10,
        }}
      >
        <div style={{ fontSize: 11, fontWeight: 600, color: t.pageText }}>Dashboard</div>
        <div
          style={{
            background: t.accent,
            color: t.accentText,
            borderRadius: 3,
            padding: '2px 7px',
            fontSize: 8,
            fontWeight: 600,
          }}
        >
          + New
        </div>
      </div>

      {/* Stat cards */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
        {[
          { label: 'Complaints', val: '12' },
          { label: 'Open', val: '4' },
          { label: 'Score', val: '87%' },
        ].map((s) => (
          <div
            key={s.label}
            style={{
              flex: 1,
              background: t.cardBg,
              border: `1px solid ${t.cardBorder}`,
              borderRadius: 5,
              padding: '6px 7px',
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 700, color: t.accent }}>{s.val}</div>
            <div style={{ fontSize: 7, color: t.cardText, marginTop: 1 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Progress bar row */}
      <div
        style={{
          background: t.cardBg,
          border: `1px solid ${t.cardBorder}`,
          borderRadius: 5,
          padding: '7px 8px',
          marginBottom: 8,
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: 4,
          }}
        >
          <span style={{ fontSize: 8, color: t.cardText }}>Compliance Score</span>
          <span style={{ fontSize: 8, color: t.accent, fontWeight: 600 }}>87%</span>
        </div>
        <div
          style={{
            height: 4,
            background: t.inputBg,
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              height: '100%',
              width: '87%',
              background: t.accent,
              borderRadius: 2,
            }}
          />
        </div>
      </div>

      {/* Mini table */}
      <div
        style={{
          background: t.cardBg,
          border: `1px solid ${t.cardBorder}`,
          borderRadius: 5,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '4px 8px',
            borderBottom: `1px solid ${t.divider}`,
            fontSize: 8,
            fontWeight: 600,
            color: t.formSubtext,
            display: 'flex',
            gap: 10,
          }}
        >
          <span style={{ flex: 2 }}>Case</span>
          <span style={{ flex: 1 }}>Status</span>
        </div>
        {[
          { case: 'WS-2026-01', status: 'Open' },
          { case: 'WS-2026-02', status: 'Closed' },
        ].map((row) => (
          <div
            key={row.case}
            style={{
              padding: '4px 8px',
              display: 'flex',
              gap: 10,
              borderBottom: `1px solid ${t.divider}`,
            }}
          >
            <span style={{ flex: 2, fontSize: 7.5, color: t.cardText }}>{row.case}</span>
            <span
              style={{
                flex: 1,
                fontSize: 7,
                color: row.status === 'Open' ? t.accent : t.formSubtext,
                fontWeight: row.status === 'Open' ? 600 : 400,
              }}
            >
              {row.status}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MiniLoginPanel({ t }: { t: ThemeConfig }) {
  return (
    <div style={{ display: 'flex', height: '100%' }}>
      {/* Left brand panel */}
      <div
        style={{
          width: '45%',
          background: t.sidebarBg,
          borderRight: `1px solid ${t.sidebarBorder}`,
          padding: '16px 12px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: t.accent, letterSpacing: 3, marginBottom: 3 }}>
            AVIKA
          </div>
          <div style={{ fontSize: 7.5, color: t.sidebarSubtext, lineHeight: 1.4 }}>
            POSH Compliance Platform
          </div>
        </div>
        <div
          style={{
            background: t.cardBg,
            border: `1px solid ${t.cardBorder}`,
            borderRadius: 6,
            padding: '8px 10px',
          }}
        >
          <div style={{ fontSize: 8, color: t.pageText, lineHeight: 1.5, fontStyle: 'italic' }}>
            &ldquo;Building safer, compliant workplaces.&rdquo;
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {['256-bit', 'ISO 27001', 'POSH Act'].map((badge) => (
            <div
              key={badge}
              style={{
                background: t.badgeBg,
                color: t.badgeText,
                borderRadius: 3,
                padding: '2px 4px',
                fontSize: 6.5,
                fontWeight: 600,
              }}
            >
              {badge}
            </div>
          ))}
        </div>
      </div>

      {/* Right form panel */}
      <div
        style={{
          flex: 1,
          background: t.formBg,
          padding: '16px 12px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: 8,
        }}
      >
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: t.formText, marginBottom: 2 }}>Sign In</div>
          <div style={{ fontSize: 7.5, color: t.formSubtext }}>Enter your credentials</div>
        </div>
        {['Email address', 'Password'].map((ph) => (
          <div
            key={ph}
            style={{
              background: t.inputBg,
              border: `1px solid ${t.inputBorder}`,
              borderRadius: 4,
              padding: '5px 8px',
              fontSize: 7.5,
              color: t.inputPlaceholder,
            }}
          >
            {ph}
          </div>
        ))}
        <div
          style={{
            background: t.accent,
            color: t.accentText,
            borderRadius: 4,
            padding: '6px 8px',
            fontSize: 8,
            fontWeight: 700,
            textAlign: 'center',
            letterSpacing: 1,
          }}
        >
          SIGN IN
        </div>
        <div style={{ fontSize: 7, color: t.formSubtext, textAlign: 'center' }}>
          Forgot password?
        </div>
      </div>
    </div>
  );
}

// ─── Theme Card ─────────────────────────────────────────────────────────────

type PreviewType = 'app' | 'login';

function ThemeCard({ t, previewType }: { t: ThemeConfig; previewType: PreviewType }) {
  const tagColors: Record<string, { bg: string; text: string }> = {
    Current: { bg: '#C4956A22', text: '#C4956A' },
    Light: { bg: '#39B5A422', text: '#39B5A4' },
    Dark: { bg: '#7B6BCC22', text: '#9B8BEC' },
    Neutral: { bg: '#768390', text: '#CDD2DA' },
  };
  const tag = tagColors[t.tag] ?? { bg: '#88888822', text: '#888' };

  return (
    <div
      style={{
        borderRadius: 12,
        overflow: 'hidden',
        border: '1px solid #2A2A2A',
        background: '#181818',
        boxShadow: '0 4px 24px rgba(0,0,0,0.4)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Preview viewport */}
      <div
        style={{
          height: 240,
          overflow: 'hidden',
          position: 'relative',
          display: 'flex',
        }}
      >
        {previewType === 'app' ? (
          <>
            <MiniSidebar t={t} />
            <MiniDashboard t={t} />
          </>
        ) : (
          <MiniLoginPanel t={t} />
        )}
      </div>

      {/* Info footer */}
      <div
        style={{
          padding: '12px 14px',
          borderTop: '1px solid #2A2A2A',
          background: '#181818',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: '#FAF8F5' }}>{t.name}</span>
          <span
            style={{
              background: tag.bg,
              color: tag.text,
              borderRadius: 3,
              padding: '1px 7px',
              fontSize: 10,
              fontWeight: 600,
            }}
          >
            {t.tag}
          </span>
        </div>
        <p style={{ margin: 0, fontSize: 11, color: '#888', lineHeight: 1.5 }}>{t.description}</p>

        {/* Colour swatches */}
        <div style={{ display: 'flex', gap: 5, marginTop: 10 }}>
          {[t.pageBg, t.sidebarBg, t.accent, t.cardBg, t.formBorder].map((color, i) => (
            <div
              key={i}
              title={color}
              style={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                background: color,
                border: '1.5px solid #333',
                flexShrink: 0,
              }}
            />
          ))}
          <span style={{ fontSize: 10, color: '#555', marginLeft: 4, alignSelf: 'center' }}>palette</span>
        </div>
      </div>
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function ThemePreviewPage() {
  const [previewType, setPreviewType] = useState<PreviewType>('app');

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#0E0E0E',
        color: '#FAF8F5',
        fontFamily: 'Inter, system-ui, sans-serif',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '24px 40px 0',
          borderBottom: '1px solid #222',
          background: '#141414',
        }}
      >
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom: 20 }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
                <span style={{ fontSize: 11, color: '#C4956A', letterSpacing: 3, fontWeight: 700 }}>AVIKA</span>
                <span style={{ color: '#333', fontSize: 12 }}>|</span>
                <span style={{ fontSize: 22, fontWeight: 700, color: '#FAF8F5' }}>Theme Preview</span>
              </div>
              <p style={{ margin: 0, fontSize: 13, color: '#777' }}>
                Compare all {THEMES.length} themes side by side &mdash; pick one and we will apply it to the full app.
              </p>
            </div>

            {/* Preview toggle */}
            <div
              style={{
                display: 'flex',
                background: '#1E1E1E',
                borderRadius: 8,
                padding: 3,
                border: '1px solid #2A2A2A',
                gap: 3,
              }}
            >
              {(['app', 'login'] as PreviewType[]).map((type) => (
                <button
                  key={type}
                  onClick={() => setPreviewType(type)}
                  style={{
                    background: previewType === type ? '#C4956A' : 'transparent',
                    color: previewType === type ? '#141414' : '#888',
                    border: 'none',
                    borderRadius: 6,
                    padding: '6px 16px',
                    fontSize: 12,
                    fontWeight: previewType === type ? 700 : 400,
                    cursor: 'pointer',
                    textTransform: 'capitalize',
                    transition: 'all 0.15s',
                  }}
                >
                  {type === 'app' ? 'App View' : 'Login View'}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Grid */}
      <div
        style={{
          maxWidth: 1200,
          margin: '32px auto',
          padding: '0 40px 60px',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
          gap: 24,
        }}
      >
        {THEMES.map((t) => (
          <ThemeCard key={t.id} t={t} previewType={previewType} />
        ))}
      </div>

      {/* Footer note */}
      <div
        style={{
          textAlign: 'center',
          padding: '20px 40px',
          borderTop: '1px solid #1A1A1A',
          fontSize: 12,
          color: '#555',
        }}
      >
        Tell me which theme you like and I will apply it across the entire app &mdash; landing page, login, and dashboard.
      </div>
    </div>
  );
}
